import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  IonButton,
  IonPage,
  IonContent,
  IonHeader,
  IonLabel,
  IonList,
  IonIcon,
  IonGrid,
  IonCol,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  IonPopover,
  IonSpinner,
} from '@ionic/react';
import { funnel } from 'ionicons/icons';
import {
  getAllProperties,
  getAllSelfEmployedInvoices,
  getSelfEmployedInvoicesStats,
} from '../../utilities/Storage';
import {storeSelfEmployedInvoices, storeSelfEmployedProperties} from '../../utilities/ApiTasks';
import { PaginatedFooter } from './PaginatedFooter';
import { PushNotificationRedirector } from '../../components/PushNotificationRedirector';
import { ClearFilter } from './ClearFilter';
import { PropertyFilter } from './PropertyFilter';
import { RequiredByDateFilter } from './RequiredByDateFilter';
import { useHistory } from 'react-router-dom';
import { ApiContext } from '../../contexts/ApiContext';
import {InvoiceRow} from "./InvoiceRow";
import {Toolbar} from "../Toolbar";
import {AuthContext} from "../../contexts/AuthContext";

export default function Invoices() {
  const { dataInitialised } = useContext(ApiContext);
   const { user } = useContext(AuthContext);
  const history = useHistory();
  const getSevenYearsAgo = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 7);

    return date.toISOString().split('T')[0];
  };

  const clampToSevenYears = function (dateStr) {
    const inputDate = new Date(dateStr);

    const today = new Date();
    const sevenYearsAgo = new Date(
        today.getFullYear() - 7,
        today.getMonth(),
        today.getDate()
    );

    if (inputDate < sevenYearsAgo) {
      return sevenYearsAgo.toISOString().split('T')[0];
    }

    return dateStr;
  }

  const sevenYearsAgo = getSevenYearsAgo();
  const [jobs, setJobs] = useState([]);
  const [allProperties, setAllProperties] = useState({});
  const [propertySearchTerm, setPropertySearchTerm] = useState('');
  const [dateOperator, setDateOperator] = useState('=>');
  const [requiredByDate, setRequiredByDate] = useState(`${sevenYearsAgo}`);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [pageLimit] = useState(20);
  const [totalJobs, setTotalJobs] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingByPull, setIsLoadingByPull] = useState(false);
  const isPaginatingRef = useRef(false);
  const [popoverState, setShowPopover] = useState({
    showPopover: false,
    event: undefined,
  });

  const createQueryStringFilterValues = useCallback((selectedProperty=propertySearchTerm, selectedDate=requiredByDate, selectedOperator=dateOperator) => ({
    status:  'Supplier Paid',
    propertyId: selectedProperty,
    invoiceapproveddatetime: selectedDate.length
        ? `${selectedOperator}${clampToSevenYears(selectedDate)}`
        : '',
  }), [propertySearchTerm, dateOperator, requiredByDate]);

  const refresh = useCallback(async (
      pageNumber = currentPageNumber,
      refreshByPull = false,
      selectedProperty = propertySearchTerm,
      selectedDate = requiredByDate,
      selectedOperator = dateOperator
  ) => {
    if (isPaginatingRef.current) return;
    isPaginatingRef.current = true;

    if(refreshByPull) {
      setIsLoadingByPull(true);
    } else {
      setIsLoading(true);
    }

    setCurrentPageNumber(pageNumber);
    setPropertySearchTerm(selectedProperty);
    setDateOperator(selectedOperator);
    setRequiredByDate(clampToSevenYears(selectedDate));

    await storeSelfEmployedInvoices(
        pageNumber,
        pageLimit,
        createQueryStringFilterValues(selectedProperty, selectedDate, selectedOperator),
        'invoiceapproveddatetime:documents:invoice'
    );

    const jobs = await getAllSelfEmployedInvoices();
    setJobs(jobs);

    const stats = await getSelfEmployedInvoicesStats();
    setTotalJobs(stats?.total?.value);
    setCurrentPageNumber(stats?.currentPage?.value);

    if(refreshByPull) {
      setIsLoadingByPull(false);
    } else {
      setIsLoading(false);
    }

    isPaginatingRef.current = false;
  }, [
    currentPageNumber,
    propertySearchTerm,
    requiredByDate,
    dateOperator,
    pageLimit,
    createQueryStringFilterValues,
  ]);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);

    if (!dataInitialised) return;

    const getData = async () => {
      if (!mounted) return;
      if(!await storeSelfEmployedProperties(user.id)) { return; }
      await storeSelfEmployedInvoices(
          currentPageNumber,
          pageLimit,
          {
            status:  'Supplier Paid',
            invoiceapproveddatetime: requiredByDate.length
                ? `${dateOperator}${clampToSevenYears(requiredByDate)}`
                : ''
          },
          'invoiceapproveddatetime:documents:invoice'
      );

      const stats = await getSelfEmployedInvoicesStats();
      const allSelfEmployedJobs = await getAllSelfEmployedInvoices();
      const properties = await getAllProperties();

      setTotalJobs(stats?.total?.value);
      setCurrentPageNumber(stats?.currentPage?.value);
      setJobs(allSelfEmployedJobs);

      if (Object.keys(allProperties).length === 0) {
        setAllProperties(properties);
      }

      setIsLoading(false);
    };

    if (dataInitialised) {
      getData();
    };

    return () => {
      mounted = false;
    };
  }, [dataInitialised]);


  let numberOfFiltersApplied = 0;
  if (propertySearchTerm > 0) {
    numberOfFiltersApplied++;
  }
  if (requiredByDate?.length > 0) {
    numberOfFiltersApplied++;
  }

  const hasFiltersApplied = numberOfFiltersApplied > 0;
  const hasNoJobs = Object.values(jobs).length === 0;
  let rowIsGrey = false;

  return (
      <IonPage>
        <IonHeader>
          <Toolbar title="Invoices" />
        </IonHeader>

        <IonContent padding>
          <IonRefresher slot='fixed' onIonRefresh={event => {
            refresh(1, true).then(() => event.detail.complete());
          }}>
            <IonRefresherContent></IonRefresherContent>
          </IonRefresher>

          <IonGrid>
            <IonRow class='ion-align-items-center'>
              <IonCol size='10'>
              </IonCol>

              <IonCol size='2'>
                <IonPopover
                    event={popoverState.event}
                    isOpen={popoverState.showPopover}
                    onDidDismiss={() => setShowPopover({ showPopover: false, event: undefined })}
                >
                  <IonGrid fixed={true}>
                    <IonRow class='ion-align-items-center'>
                      <IonCol size='12'>
                        <PropertyFilter
                            properties={allProperties}
                            value={propertySearchTerm}
                            onChange={(propertySearchTerm) => {
                              refresh(1, false, propertySearchTerm, requiredByDate, dateOperator);
                            }}
                        />
                      </IonCol>
                    </IonRow>

                    <IonRow class='ion-align-items-center'>
                      <IonCol size='12'>
                        <RequiredByDateFilter
                            operator={dateOperator}
                            date={requiredByDate}
                            minLimit={sevenYearsAgo}
                            onOperatorChange={(dateOperator) => {
                              requiredByDate
                                  ? refresh(1, false, propertySearchTerm, requiredByDate, dateOperator)
                                  : setDateOperator(dateOperator);
                            }}
                            onRequiredByDateChange={(requiredByDate) => {
                              refresh(1, false, propertySearchTerm, requiredByDate, dateOperator);
                            }}
                        />
                      </IonCol>
                    </IonRow>

                    <IonRow>
                      <IonCol className='ion-text-right'>
                        <ClearFilter
                            onClick={() => {
                              refresh(1, false, '', '', '=');
                              setShowPopover({ showPopover: false, event: undefined });
                            }}/>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonPopover>

                <IonButton
                    color='fern'
                    size='medium'
                    expand='block'
                    onClick={(e) =>
                        setShowPopover({ showPopover: true, event: e.nativeEvent })
                    }
                >
                  <IonIcon icon={funnel} size='small' />
                  {hasFiltersApplied && <IonLabel> <small>{numberOfFiltersApplied}</small></IonLabel>}
                </IonButton>
              </IonCol>
            </IonRow>
          </IonGrid>

          {isLoading ? (
              <div className='spinner-container'>
                <IonSpinner color='teal' />
              </div>
          ) : (

              <IonList lines='full'>
                {hasNoJobs ? (
                    <h3 key='nojobs' className='ion-text-center'>No invoices found.</h3>
                ) : (
                    jobs.map((job) =>{
                      rowIsGrey = !rowIsGrey;
                      const color = rowIsGrey ? 'lgrey' : 'white';
                      return (
                          <InvoiceRow
                              key={job.value.id}
                              color={color}
                              job={job.value}
                              history={history}
                          />
                      );
                    })
                )
                }
              </IonList>
          )}
        </IonContent>
        <PaginatedFooter
            currentPageNumber={currentPageNumber}
            itemsCount={totalJobs}
            pageLimit={pageLimit}
            onPageChange={(page) => {
              refresh(page, false);
            }}
            isLoading={isLoading}
        />

        <PushNotificationRedirector />
      </IonPage>
  );
}
